import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Link,
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/core";
import { css } from "@emotion/core";
import React, { useRef, useState } from "react";

import logoIcon from "../../assets/logo.png";
import {
  hasRulesLink,
  headerImageUrl,
  ICompetition,
  logoImageUrl,
} from "../../services/competition-consent.service";

// Ha a játékhoz nincs feltöltött fejléckép, márkaszínű átmenet lép a helyére —
// az ablak működőképes marad.
const HEADER_FALLBACK = "linear-gradient(135deg,#3a2a1c 0%,#da1515 100%)";

const GREEN = "#2e9e62";
const IDLE_BORDER = "#d9dde1";
const SELECTED_BG = "#eefaf2";
const DISABLED_BTN = "#c7ccd1";
const RED = "#e8322a";


function ConsentOption({
  text,
  isSelected,
  isBold,
  onClick,
}: {
  text: string;
  isSelected: boolean;
  isBold?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <Flex
      onClick={onClick}
      cursor="pointer"
      p="14px 16px"
      borderRadius="12px"
      border="2px solid"
      borderColor={isSelected ? GREEN : IDLE_BORDER}
      backgroundColor={isSelected ? SELECTED_BG : "#fff"}
    >
      <Flex
        flex="none"
        w="22px"
        h="22px"
        mt="1px"
        mr="12px"
        borderRadius="6px"
        alignItems="center"
        justifyContent="center"
        border="2px solid"
        borderColor={GREEN}
        backgroundColor={isSelected ? GREEN : "transparent"}
        color="#fff"
        fontSize="14px"
        fontWeight={800}
      >
        {isSelected ? "✓" : ""}
      </Flex>
      <Text fontSize="14.5px" lineHeight="1.45" fontWeight={isBold ? 600 : 400}>
        {text}
      </Text>
    </Flex>
  );
}

// Nyereményjáték: kötelező hozzájárulás. Bejelentkezés után és a megrendelés
// előtt is ez az ablak jelenik meg, amíg a felhasználó nem válaszolt — ezért
// nincs bezáró gomb, és sem az ESC, sem a háttérre kattintás nem zárja. A felső
// menüből újranyitva viszont bezárható (isDismissible).
//
// Minden szöveg és a fejléckép a `competition`-ből jön, tehát a szerverről.
function CompetitionConsentModal({
  isOpen,
  competition,
  onDecide,
  onClose,
  isDismissible = false,
  initialAccepted = null,
  isChange = false,
}: {
  isOpen: boolean;
  competition: ICompetition;
  onDecide: (accepted: boolean) => Promise<void>;
  onClose: () => void;
  isDismissible?: boolean;
  initialAccepted?: boolean | null;
  isChange?: boolean;
}): JSX.Element {
  const [selected, setSelected] = useState<boolean | null>(initialAccepted);
  const [isSaving, setIsSaving] = useState(false);

  // Megnyitáskor a Chakra az első fókuszálható elemre ugrik — itt ez a
  // szabályzat-link volt, amit a fókuszgyűrű kijelöltnek mutatott, mintha a
  // felhasználó már választott volna. A fókuszt ezért magára az ablakra
  // irányítjuk (tabIndex -1), így nyitáskor semmi nincs kiemelve, a
  // billentyűzetes navigáció viszont változatlanul működik.
  const contentRef = useRef<HTMLElement>(null);

  const headerUrl = headerImageUrl(competition);
  const logoUrl = logoImageUrl(competition);

  async function submit() {
    if (selected === null || isSaving) return;
    setIsSaving(true);
    await onDecide(selected);
    setIsSaving(false);
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="560px"
      closeOnOverlayClick={isDismissible}
      closeOnEsc={isDismissible}
      initialFocusRef={contentRef}
    >
      {/* A háttér elmosása a mobilapp modáljainak megjelenését követi
          (`blur_dialog.dart`, sigma 8). A sötét fátyol emiatt halványabb: a
          blur maga is elválasztja a hátteret, tömény fátyollal nem is látszana.
          A -webkit- előtag a Safari miatt kell. */}
      <ModalOverlay
        backgroundColor="rgba(10,10,10,.45)"
        css={css`
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        `}
      />
      <ModalContent
        ref={contentRef}
        tabIndex={-1}
        borderRadius="20px"
        overflow="hidden"
        color="#1c1c1c"
      >
        <Box
          position="relative"
          height="200px"
          backgroundColor="#3a2a1c"
          backgroundImage={
            headerUrl
              ? `linear-gradient(180deg,rgba(0,0,0,.05) 0%,rgba(0,0,0,.45) 100%), url(${headerUrl})`
              : HEADER_FALLBACK
          }
          backgroundSize="cover"
          backgroundPosition="left 28%"
          backgroundRepeat="no-repeat"
        >
          <Flex position="absolute" left="28px" bottom="20px" alignItems="center">
            {/* Feltöltött kampány-logó esetén azt mutatjuk, egyébként a
                beépítettet. A logó aránya előre nem ismert, ezért `contain`,
                és a mérete korlátozott, hogy a név is kiférjen mellé. */}
            {logoUrl ? (
              <Image
                src={logoUrl}
                maxHeight="120px"
                maxWidth="200px"
                objectFit="contain"
                mr="14px"
              />
            ) : (
              <Image src={logoIcon} height="82px" width="auto" mr="14px" />
            )}
            <Text
              color="#fff"
              fontSize="19px"
              fontWeight={800}
              letterSpacing=".14em"
              textTransform="uppercase"
            >
              {competition.name}
            </Text>
          </Flex>
        </Box>

        <Box p="26px 28px 28px">
          <Heading as="h2" fontSize="25px" lineHeight="1.2" fontWeight={800}>
            {competition.title}
          </Heading>

          <Box mt="18px">
            <ConsentOption
              text={competition.acceptText}
              isSelected={selected === true}
              onClick={() => !isSaving && setSelected(true)}
            />
            <Box h="10px" />
            <ConsentOption
              text={competition.declineText}
              isBold
              isSelected={selected === false}
              onClick={() => !isSaving && setSelected(false)}
            />
          </Box>

          {hasRulesLink(competition) && (
            <Link
              href={competition.rulesUrl}
              isExternal
              display="block"
              mt="18px"
              fontSize="14px"
              fontWeight={700}
              color={RED}
              textDecoration="underline"
            >
              {competition.linkText}
            </Link>
          )}

          <Flex mt="18px" alignItems="center" justifyContent="space-between">
            <Text
              fontSize="12.5px"
              maxW="250px"
              lineHeight="1.35"
              color={selected === null ? "#8a9098" : "#5a6069"}
            >
              {selected === null
                ? "A továbblépéshez válassz a két lehetőség közül."
                : "Kiválasztva — mehet tovább."}
            </Text>
            <Button
              onClick={submit}
              isDisabled={selected === null || isSaving}
              isLoading={isSaving}
              borderRadius="10px"
              p="14px 34px"
              height="auto"
              fontSize="16px"
              fontWeight={800}
              color="#fff"
              backgroundColor={selected === null ? DISABLED_BTN : GREEN}
              _hover={{ backgroundColor: selected === null ? DISABLED_BTN : "#268252" }}
            >
              {isChange ? "Mentés" : "Tovább"}
            </Button>
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
}

export default CompetitionConsentModal;
